/**
 * The loading/error/empty/content helper every Batch 2 screen is expected to use rather than
 * reinventing its own spinner and its own error banner nine times.
 *
 * `renderAsync` owns exactly one promise's lifecycle against one container element:
 *   - paints a loading state immediately (synchronously, before the promise settles)
 *   - on success, paints empty or content depending on `isEmpty`
 *   - on failure, paints an error state with a retry button that re-runs `load`
 *   - if the view is disposed before `load()` settles, the result is discarded — nothing
 *     paints into a container the caller has already torn down or reused for something else
 *
 * This mirrors the same "unmounted flag checked in the .then" discipline `bootstrap.ts` uses
 * for the armoury's dynamic import, generalised to any promise.
 */
import { button, icon } from './ui';

export type AsyncViewState = 'loading' | 'error' | 'empty' | 'content';

export interface AsyncViewOptions<T> {
  /** Fetches (or re-fetches, on retry) the data for this view. */
  load: () => Promise<T>;
  /** Renders the resolved data. May return its own disposer (e.g. an interval, a preview). */
  renderContent: (data: T, container: HTMLElement) => (() => void) | void;
  /** True when `data` should be treated as "nothing to show" rather than content. */
  isEmpty?: (data: T) => boolean;
  renderLoading?: (container: HTMLElement) => void;
  renderError?: (container: HTMLElement, error: unknown, retry: () => void) => void;
  renderEmpty?: (container: HTMLElement) => void;
}

/** A floating 3D icon over one line of text — the fallback states are pictures, not paragraphs. */
function statusBlock(art: Parameters<typeof icon>[0], text: string, textClass: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'flex flex-col items-center gap-2 py-8 text-center';
  wrap.append(icon(art, { size: 56, className: 'kit-float [filter:drop-shadow(0_6px_8px_rgba(0,0,0,0.6))]' }));
  const line = document.createElement('p');
  line.className = textClass;
  line.textContent = text;
  wrap.append(line);
  return wrap;
}

function defaultLoading(container: HTMLElement): void {
  container.innerHTML = '';
  container.append(statusBlock('time', 'Loading…', 'kit-label font-heading uppercase text-sm motion-safe:animate-pulse'));
}

function defaultError(container: HTMLElement, error: unknown, retry: () => void): void {
  container.innerHTML = '';
  // The failure sits on a painted plate with the retry key beneath it.
  const plate = document.createElement('div');
  plate.className = 'kit-plate [--kp-rim:14px] [--kp-pad:18px] flex flex-col items-center gap-3 text-center';
  const message = document.createElement('p');
  message.className = 'text-sm text-error';
  message.textContent = error instanceof Error ? error.message : 'Something went wrong.';
  plate.append(icon('locked', { size: 48 }), message, button('Retry', { variant: 'secondary', onClick: retry }));
  container.append(plate);
}

function defaultEmpty(container: HTMLElement): void {
  container.innerHTML = '';
  container.append(statusBlock('crate', 'Nothing here yet.', 'text-sm text-text-dim'));
}

/**
 * Mounts a loading/error/empty/content view into `container` and returns a disposer.
 *
 * The returned disposer is safe to call at any point in the lifecycle — before, during, or
 * after the promise settles — and calling it twice is a no-op.
 */
export function renderAsync<T>(container: HTMLElement, opts: AsyncViewOptions<T>): () => void {
  let disposed = false;
  let contentTeardown: (() => void) | null = null;

  function clearContent(): void {
    contentTeardown?.();
    contentTeardown = null;
  }

  function run(): void {
    clearContent();
    (opts.renderLoading ?? defaultLoading)(container);

    opts
      .load()
      .then((data) => {
        if (disposed) return;
        clearContent();
        if (opts.isEmpty?.(data)) {
          (opts.renderEmpty ?? defaultEmpty)(container);
          return;
        }
        contentTeardown = opts.renderContent(data, container) ?? null;
      })
      .catch((error: unknown) => {
        if (disposed) return;
        clearContent();
        (opts.renderError ?? defaultError)(container, error, run);
      });
  }

  run();

  return () => {
    if (disposed) return;
    disposed = true;
    clearContent();
  };
}
